import React, { useEffect, useState, Fragment } from 'react';
import { Table, Spin, Button, DatePicker, message, Input, Select } from 'antd';
import { getFtaDataService } from '../../../services/FTAReportService';
import { getAllAdminIECsService } from '../../../services/FileDataService';
import { getBRCorelationService } from '../../../services/MasterService';
import moment from 'moment';
import { LoadingOutlined } from '@ant-design/icons';
import { Excel } from 'antd-table-saveas-excel';
import { Col, Row } from 'react-bootstrap';
import Card from '../../../components/bootstrap/card';
import { getTodaysDate, formatToTwoDecimals } from '../../../utilities/utils';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import ReportDateFilter from '../../../components/ReportDateFilter';

import useColumnVisibility from '../../../hooks/useColumnVisibility';
import ColumnSelector from '../../../components/common/ColumnSelector';

const { RangePicker } = DatePicker;
const FtaReport = () => {
  // const iec_no = localStorage.getItem('iec_no');
  const [jsonData, setJsonData] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIECName, setSelectedIECName] = useState(localStorage.getItem('name'))
  const [isValid, setIsValid] = useState(true); // Validation state
  const [billNo, setBillNo] = useState("");
  const [branchCodeNames, setBranchCodeNames] = useState([]);
  const [selectedBranchCodes, setSelectedBranchCodes] = useState([]);

  const location = useLocation();
  const currentYear = dayjs().year();
  const defaultFrom = dayjs(`${currentYear}-01-01`);
  const defaultTo = dayjs(`${currentYear}-12-31`);
  const [dates, setDates] = useState(() => {
    // if report is opened through navigation from dashboard, then use those dates else default
  if (location.state?.passedDates?.length === 2) {
    return [
      dayjs(location.state.passedDates[0]),
      dayjs(location.state.passedDates[1]),
    ];
  }
  return [defaultFrom, defaultTo];
});

  const [IECOptions, setIECOptions] = useState([]);
  const userRole = localStorage.getItem('role'); 
  const [selectedIEC, setSelectedIEC] = useState(
    localStorage.getItem('iec_no')
  );

  // ─── TABLE STYLES ────────────────────────────────────────────────────────────
  const tableStyles = `
    .fta-table .ant-table-thead > tr > th {
      white-space: normal !important;
      word-break: break-word !important;
      overflow: visible !important;
      text-overflow: unset !important;
      vertical-align: middle !important;
      line-height: 1.3 !important;
      padding-top: 8px !important;
      padding-bottom: 8px !important;
    }
    .fta-table .ant-table-tbody > tr > td {
      white-space: nowrap;
    }
    .fta-table .ant-table-container {
      overflow-x: auto !important;
    }
    .fta-table .ant-table-header {
      overflow: hidden !important;
    }
    .fta-table .ant-table-sticky-holder {
      overflow: hidden !important;
    }
  `;

  // Define columns for the table
  const columns = [
    {
      title: 'BUSINESS UNIT',
      dataIndex: 'business_unit',
      key: 'business_unit',
      header: 'BUSINESS UNIT',
      width: 160
    , fixed: 'left' },
    {
      title: 'PLANT NAME',
      dataIndex: 'plant_name',
      key: 'plant_name',
      header: 'PLANT NAME',
      width: 150
    , fixed: 'left' },
    {
      title: 'IEC/BR',
      dataIndex: 'iec_br',
      key: 'iec_br',
      header: 'IEC/BR',
      width: 150
    },
    {
      title: 'PORT CODE',
      dataIndex: 'port_code',
      key: 'port_code',
      header: 'PORT CODE',
      width: 115
    },
    {
      title: 'FY',
      dataIndex: 'fy',
      key: 'fy',
      header: 'FY',
      width: 80
    },
    {
      title: 'BOE NO.',
      dataIndex: 'be_no',
      key: 'be_no',
      header: 'BOE NO.',
      width: 115
    },
    {
      title: 'BOE DT',
      dataIndex: 'be_date',
      key: 'be_date',
      header: 'BOE DT',
      width: 110,
      render: (text, record) => {
        if (record.be_date) {
          return formatDateToDDMMYY(record.be_date);
        }
      }
    },
    {
      title: 'REF CODE.',
      dataIndex: 'reference_code',
      key: 'reference_code',
      header: 'REF CODE.',
      width: 115
    },
    {
      title: 'COUNTRY CODE',
      dataIndex: 'coo',
      key: 'coo',
      header: 'COUNTRY CODE',
      width: 140
    },
    {
      title: 'HSN CODE',
      dataIndex: 'cth_item_detail',
      key: 'cth_item_detail',
      header: 'HSN CODE',
      width: 115
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'description',
      key: 'description',
      header: 'DESCRIPTION',
      width: 130
    },
    {
      title: 'ASSE. VALUE IN INR',
      dataIndex: 'assess_value',
      key: 'assess_value',
      header: 'ASSE. VALUE IN INR',
      align: 'right',
      width: 185,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'BCD RATE',
      dataIndex: 'bcd_rate',
      key: 'bcd_rate',
      header: 'BCD RATE',
      align: 'right',
      width: 110,
      render: (text, record) => {
        return formatToTwoDecimals(text) + '%'
      }
    },
    {
      title: 'BCD AMT',
      dataIndex: 'bcd_amount',
      key: 'bcd_amount',
      header: 'BCD AMT',
      align: 'right',
      width: 110,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'FTA BCD RATE',
      dataIndex: 'rate_of_duty_percent',
      key: 'rate_of_duty_percent',
      header: 'FTA BCD RATE',
      align: 'right',
      width: 140,
      render: (text, record) => {
        return formatToTwoDecimals(text) + '%'
      }
    },
    {
      title: 'APPLI. DUTY',
      dataIndex: 'applied_duty',
      key: 'applied_duty',
      header: 'APPLI. DUTY',
      align: 'right',
      width: 130,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'BCD SAVING(Cumulative Cess & IGST is not considered)',
      dataIndex: 'duty_saving',
      key: 'duty_saving',
      header: 'BCD SAVING(Cumulative Cess & IGST is not considered)',
      align: 'right',
      width: 480,
      render: (text, record) => {
        return formatToTwoDecimals(text)
      }
    },
    {
      title: 'NOTI. NO. & DATE',
      dataIndex: 'notification_no',
      key: 'notification_no',
      header: 'NOTI. NO. & DATE',
      width: 175
    },
    {
      title: 'ITEM SR. NO.',
      dataIndex: 'item_sr_no',
      key: 'item_sr_no',
      header: 'ITEM SR. NO.',
      align: 'right',
      width: 135
    },
    {
      title: 'AGREEMENT',
      dataIndex: 'agreement',
      key: 'agreement',
      header: 'AGREEMENT',
      width: 125
    }
  ];

  const { visibleKeys, setVisibleKeys, filteredColumns, resetToDefault } =
    useColumnVisibility('FTAReport', columns);

  const fetchData = async () => {
    const filters = {
      from_date: dates?.[0] ? dayjs(dates[0]).format("YYYY-MM-DD") : null,
      to_date: dates?.[1] ? dayjs(dates[1]).format("YYYY-MM-DD") : null,
      bill_no: billNo || null,
      br_code: selectedBranchCodes || null,
    };
    setLoading(true);
    try {
      // const response = await getFtaDataService(iec_no, filters);
      const response = await getFtaDataService(selectedIEC, filters);

      setJsonData(response);
    } catch (error) {
      setError('Error fetching FTA data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userRole === 'superadmin' || userRole === 'group_head') {
      getAdminIEC();
    }
    if (userRole === 'admin' || userRole === 'user') {
      fetchData();
    }
    getBRCorelationService(selectedIEC).then((response) => {
      setBranchCodeNames(response);
    });
  }, []);

  const preprocessData = (data) => {
    return data.map(record => {
      if (record.be_date) {
        const beDate = new Date(record.be_date);
        // Extract fiscal year from sb_date
        const fiscalYearStartYear = beDate.getMonth() >= 3 ? beDate.getFullYear() : beDate.getFullYear() - 1;
        const fiscalYearEndYear = fiscalYearStartYear + 1;
        const fiscalYear = `AM${fiscalYearEndYear.toString().slice(-2)}`;
        record.FY = fiscalYear;
        // Format sb_date

        record.beDate = formatDateToDDMMYY(beDate);
      } else {
        record.FY = '';
        record.be_date = '';
        record.beDate = '';
      }
      return record;
    });
  };
  function formatDateToDDMMYY(dateString) {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return ''; // Return empty string if the dateString is not a valid date
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  }

  const getDateOnly = (dateString) => {
    if (dateString && dateString.length !== 0) {
      const date = new Date(dateString);
      return date;
    }
    else {
      return '';
    }
  }

  const handleDateChange = (value) => {
    setDates(value || []);
    if (value && value.length === 2) {
      const from_date = value[0];
      const to_date = value[1];

      // Validate that both dates are selected
      if (!from_date || !to_date) {
        setIsValid(false);
        return;
      }

      // Validate that the duration is within 1 year
      const oneYearLater = from_date.add(1, 'year');
      if (to_date.isAfter(oneYearLater)) {
        message.error("The selected duration should not exceed 1 year.");
        setIsValid(false);
        return;
      }

      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };

  const getAdminIEC = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await getAllAdminIECsService(token);

      if (response?.data) {
        const options = response.data.map((item, index) => ({
          value: `${item.iec_no};${item.fullname}`,
          label: `${item.iec_no} (${item.fullname})`,
          key: index,
        }));
        setIECOptions(options);
      }
    } catch (err) {
      message.error("Failed to load IEC list");
    }
  };

  const handleSubmit = () => {
    if (!selectedIEC) {
      message.error("Please select an IEC");
      return;
    }

    if (dates.length === 2 && isValid) {
      fetchData();
    } else {
      message.error("Please select a valid date range");
    }
  };

const handleIECSelection = (val) => {
  if (val) {
    const [iec_no, iec_name] = val.split(";");
    setSelectedIEC(iec_no);
    setSelectedIECName(iec_name);
  } else {
    setSelectedIEC(null);
    setSelectedIECName('');
  }
};

  const handleBillSearch = (value) => {
    setBillNo(value);
  };

  const handleBranchCodeSelection = (values) => {
    setSelectedBranchCodes(values);
  };

  const exportToExcel = async () => {
    let workbook = new ExcelJS.Workbook();
    let worksheet = workbook.addWorksheet('Data');
    var data = jsonData;

    // Define columns for Excel export

    worksheet.columns = filteredColumns.map(col => ({
      header: col.header || col.title,
      key: col.dataIndex,
      width: 20
    }));
    // Apply styles to the header row (Row 1)
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true }; // Bold text
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFC000' }, // Yellow background
      };
      cell.alignment = { vertical: 'middle' }; // Center align
    });

    data.forEach((obj) => {
      worksheet.addRow({
        ...obj,
        be_date: getDateOnly(obj.be_date),
        leo_date: getDateOnly(obj.leo_date),
        bcd_rate: obj.bcd_rate ? parseFloat(obj.bcd_rate) : '',
        bcd_amount: obj.bcd_amount ? parseFloat(obj.bcd_amount) : '',
        scroll_amount: obj.scroll_amount ? parseFloat(obj.scroll_amount) : '',
        applied_duty: obj.applied_duty ? parseFloat(obj.applied_duty) : '',
        assess_value: obj.assess_value ? parseFloat(obj.assess_value) : ''
      })

    });

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    // Use FileSaver to save the file
    saveAs(blob, `Lucrative's CIP DSS - FTA Opportunities as on ${getTodaysDate()} of ${selectedIEC}.xlsx`);
  };

  return (
    <Fragment>
      <style>{tableStyles}</style>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <div className="header-title">
                <h4 className="card-title">Lucrative's CIP DSS - FTA Opportunities as on {moment().format('DD/MM/YYYY')} of {selectedIECName}</h4>
                <p><i>Note: Subject to FTA/PTA & DGFT conditions fulfilment.</i></p>
              </div>
              <div className="d-flex align-items-center gap-2">
                <ColumnSelector
                  allColumns={columns}
                  visibleKeys={visibleKeys}
                  setVisibleKeys={setVisibleKeys}
                  onReset={resetToDefault}
                />
                <button
                  className='btn btn-primary cip-btn-submit'
                  onClick={exportToExcel}
                >
                  Export to Excel
                </button>
              </div>
            </Card.Header>
            <Card.Body className="d-flex justify-content-start align-items-center gap-2 ps-4">
              {(userRole === 'superadmin' || userRole === 'group_head') && (
                <Select
                  showSearch
                  placeholder="Select IEC"
                  onChange={handleIECSelection}
                  allowClear
                  options={IECOptions}
                  style={{ width: '300px' }}
                />
              )}
              <ReportDateFilter dates={dates} onChange={handleDateChange} />
              <Input
                placeholder="Enter Bill No"
                allowClear
                value={billNo}
                onChange={(e) => handleBillSearch(e.target.value)}
                style={{ width: 250 }}
              />
              <Select
                mode="multiple"
                allowClear
                style={{ width: 250 }}
                placeholder="Select Branch Code"
                onChange={handleBranchCodeSelection}
                options={branchCodeNames.map((item) => ({
                  value: item.branch_code,
                  label: `${item.branch_code} / ${item.plant_name} (${item.business_unit})`,
                }))}
              />
              <Button type="primary" className="cip-btn-submit"
                onClick={handleSubmit}>
                Submit
              </Button>
            </Card.Body>
            <Card.Body className="px-0 report-page">
              <Table
                className="fta-table"
                bordered
                columns={filteredColumns}
                dataSource={jsonData}
                loading={isLoading}
                scroll={{ x: 'max-content' }}
                
                pagination={{ showTotal: (total) => `Total ${total} records`, showSizeChanger: true }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default FtaReport;
